import { useNavigate, useLocation } from 'react-router-dom';
import { Scale } from 'lucide-react';
import { CASE_TYPE_PAGES } from '@/data/caseTypePages';
import styles from './LandingFooter.module.css';

const LandingFooter = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (location.pathname !== '/') {
      navigate('/#' + id);
    }
  };

  // Homepage section anchors resolve in place on `/` and fall back to a route
  // change everywhere else.
  const anchor = (id) => ({
    href: `/#${id}`,
    onClick: (e) => {
      e.preventDefault();
      scrollTo(id);
    },
  });

  const route = (path) => ({
    href: path,
    onClick: (e) => {
      e.preventDefault();
      navigate(path);
    },
  });

  const column = (heading, items) => (
    <div>
      <h2 className={styles.heading}>{heading}</h2>
      <ul className={styles.list}>
        {items.map((item) => (
          <li key={item.label}>
            <a className={styles.link} {...item.props}>
              {item.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <footer className={styles.footer} data-testid="landing-footer">
      <div className={styles.inner}>
        <div className={styles.grid}>
          <div>
            <button
              type="button"
              className={styles.logo}
              onClick={() => navigate('/')}
              aria-label="MyCaseLoan home"
            >
              <Scale size={24} aria-hidden="true" />
              <span className={styles.logoText}>MyCaseLoan</span>
            </button>
            <p className={styles.blurb}>
              A marketplace where verified funders compete to fund your case, so you
              don&rsquo;t have to take the first offer you&rsquo;re given.
            </p>
          </div>

          {column('Get funded', [
            { label: 'Apply now', props: route('/apply') },
            { label: 'How it works', props: anchor('how') },
            { label: 'What it costs', props: anchor('cost') },
            { label: 'FAQ', props: route('/faq') },
          ])}

          {column(
            'Case types',
            CASE_TYPE_PAGES.map((page) => ({
              label: page.badge.replace(' Funding', ''),
              props: route(page.path),
            })),
          )}

          {column('Partners', [
            { label: 'For law firms', props: route('/for-law-firms') },
            { label: 'For funders', props: route('/for-funders') },
            { label: 'Log in', props: route('/login') },
          ])}

          {column('Company', [
            { label: 'Contact', props: route('/contact') },
            { label: 'Terms', props: route('/terms') },
            { label: 'Privacy', props: route('/privacy') },
          ])}
        </div>

        <div className={styles.bottom}>
          <span>&copy; {new Date().getFullYear()} MyCaseLoan.com</span>
        </div>

        {/* Legally reviewed copy — REDESIGN.md §4.8. Do not reword. */}
        <p className={styles.note}>
          MyCaseLoan is a technology platform, not a funding company, lender, or law
          firm, and does not provide legal advice. Pre-settlement funding is a
          non-recourse purchase of a portion of the proceeds of your claim.
          Availability and terms vary by state and by funder. Figures shown on this
          page are illustrative examples only.
        </p>
      </div>
    </footer>
  );
};

export default LandingFooter;
